import * as React from 'react';
import {
  useMergeRefs,
  FloatingFocusManager,
  FloatingOverlay,
  type Side,
  FloatingNode,
} from '@floating-ui/react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {useThemeContext} from '@packages/utilities/styled';

import {FocusTrap} from '../FocusTrap';
import {Mask} from '../Mask';
import {OverlayPortal} from '../OverlayPortal';

import {
  PopoverContext,
  usePopoverContext,
  usePopoverTransition,
  type PopoverContentColorVariant,
} from './usePopover';
import {
  PopoverArrowView,
  PopoverArrowShadow,
  PopoverContentView,
  PopoverWrapperView,
} from './Popover.styles';

import type {HTMLAttributes} from '../__SharedTypes';

export interface PopoverCardProps extends HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  /*
   * The background color of the `PopoverContent`. There are maybe rare cases where the background color
   * of the `Popover` would change from the system default. An example may include education or promotional purposes.
   */
  colorVariant?: PopoverContentColorVariant;
  /**
   * Increases the border-radius on the `PopoverContent` when `true`.
   */
  hasSoftCorners?: boolean;
  /**
   * Removes the inner padding from the `PopoverContent` container.
   */
  isSeamless?: boolean;
  /**
   * Renders an arrow that points to the `Popover`'s anchor. By default, we don't render the arrow.
   */
  hasArrow?: boolean;
  /**
   * Whether the Popover should be animated when opening and closing.
   */
  shouldAnimate?: boolean;
}

export const PopoverContent = React.forwardRef<
  HTMLDivElement,
  PopoverCardProps
>(
  (
    {
      colorVariant = 'default',
      children,
      hasArrow,
      hasSoftCorners = false,
      isSeamless = false,
      shouldAnimate = false,
      ...props
    }: PopoverCardProps,
    propRef
  ) => {
    const popoverContext = usePopoverContext();
    const {
      arrowRef,
      context,
      maskProps,
      maxHeight,
      minHeight,
      refs,
      returnFocus,
      shouldPreventBodyScroll,
      shouldShowMask = false,
      shouldTrapFocus = true,
      nodeId,
    } = popoverContext;

    const {
      isMounted,
      transitionStyles,
      x,
      y,
      strategy,
      setFloating,
      getFloatingProps,
    } = usePopoverTransition({shouldAnimate});

    const ref = useMergeRefs([setFloating, propRef]);
    const {__internalTheme} = useThemeContext();

    const popoverContextValue = React.useMemo(
      () => ({...popoverContext, colorVariant}),
      [popoverContext, colorVariant]
    );

    if (!isMounted) return null;

    /**
     * We need to "continue" the box-shadow of our TooltipContentWrapper.
     * It uses inset box shadows. SVGs do not have box-shadow, only drop-shadow. Drop shadows do not have an inset property.
     * In addition, the shadows are dark in both light & dark theme.
     *
     * This means that we need to render out special shadows for our arrows, and that they need to be different between the themes integrate in their respective backgrounds properly.
     *
     * For dark theme, we are using a second arrow, which renders its own drop-shadow.
     * For light theme, we need do not have this shadow arrow. Instead, we use a filter to drop-shadow the entire tooltip, giving a subtle background for the arrow to show up.
     * */
    const isDarkTheme = __internalTheme?.toLowerCase?.()?.includes('dark');
    const currentPlacement = (context?.placement?.replace(/-(start|end)/, '') ??
      'bottom') as Side;

    const isLight = __internalTheme?.toLocaleLowerCase?.()?.includes('light');

    const {colorVariant: maskColorVariant = 'default', ...restMaskProps} =
      maskProps ?? emptyObject;

    const maskType =
      maskColorVariant === 'transparent' ? 'transparent' : 'popover';

    const anchorCutoutRef = refs.domReference;

    const isModal = shouldTrapFocus && shouldShowMask;

    /**
     * We need to stop our wheel events from propagating so Popovers in style panel don't trigger the `ScrollView` component
     */
    const handleOnWheel = (e: React.WheelEvent<HTMLElement>) => {
      e.stopPropagation();
    };

    return (
      <FloatingNode id={nodeId}>
        <OverlayPortal>
          <FloatingOverlay
            lockScroll={shouldPreventBodyScroll}
            style={{pointerEvents: 'none'}}
          />
          {shouldShowMask && (
            <Mask
              {...restMaskProps}
              anchorCutoutRef={anchorCutoutRef}
              shouldAllowPointerEvents={false}
              type={maskType}
            />
          )}
          <FloatingFocusManager
            closeOnFocusOut
            context={context}
            modal={isModal}
            returnFocus={returnFocus}
            visuallyHiddenDismiss
          >
            <FocusTrap shouldTrapFocus={shouldTrapFocus}>
              <PopoverContext.Provider value={popoverContextValue}>
                <PopoverWrapperView
                  {...(isModal && {'aria-modal': true})}
                  colorVariant={colorVariant}
                  hasSoftCorners={hasSoftCorners}
                  lightModeShadowPlacement={
                    isLight && hasArrow ? currentPlacement : undefined
                  }
                  onWheel={handleOnWheel}
                  ref={ref}
                  style={{
                    top: y,
                    left: x,
                    minHeight,
                    width: 'max-content',
                    position: strategy,
                    ...(shouldAnimate && transitionStyles),
                  }}
                  {...getFloatingProps(props)}
                >
                  {hasArrow ? (
                    <PopoverArrowView
                      colorVariant={colorVariant}
                      context={context}
                      data-automation-id="popover-arrow"
                      ref={arrowRef as React.MutableRefObject<SVGSVGElement>} // @SPRING_CLEANING: We are typecasting this because ArrowOptions['element'] is typed differently than the ref used for FloatingArrow. See if we can resolve this, if possible.
                    />
                  ) : null}
                  {/* To achieve the dropshadow effect, we place a second arrow (w/ shadows) and bump it just a tad  */}
                  {hasArrow && isDarkTheme ? (
                    <>
                      <PopoverArrowShadow
                        colorVariant={colorVariant}
                        context={context}
                        data-arrow="true"
                        placement={currentPlacement}
                        ref={arrowRef as React.MutableRefObject<SVGSVGElement>} // @SPRING_CLEANING: We are typecasting this because ArrowOptions['element'] is typed differently than the ref used for FloatingArrow. See if we can resolve this, if possible.
                        style={{
                          transform: 'translateY(-1px)', // bump it down to lay the shadow over the base arrow
                        }}
                      />
                    </>
                  ) : null}
                  {/* This second arrow is used to mask the content wrapper's shadows, as well as the bottom half of the ArrowShadow's drop-shadow if we're in dark mode */}
                  {hasArrow ? (
                    <PopoverArrowView
                      colorVariant={colorVariant}
                      context={context}
                      data-arrow="true"
                      ref={arrowRef as React.MutableRefObject<SVGSVGElement>} // @SPRING_CLEANING: We are typecasting this because ArrowOptions['element'] is typed differently than the ref used for FloatingArrow. See if we can resolve this, if possible.
                      style={{
                        transform: 'translateY(-2px)', // bump this down to cover the shadow arrow's bottom shadow transition.
                      }}
                    />
                  ) : null}
                  <PopoverContentView
                    hasSoftCorners={hasSoftCorners}
                    isSeamless={isSeamless}
                    style={{maxHeight, minHeight}}
                  >
                    {children}
                  </PopoverContentView>
                </PopoverWrapperView>
              </PopoverContext.Provider>
            </FocusTrap>
          </FloatingFocusManager>
        </OverlayPortal>
      </FloatingNode>
    );
  }
);
